import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Users, Zap, Heart, Award, ShieldCheck, UserCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const values = [
  { icon: Target, title: 'Excellence', description: 'We strive for excellence in everything we do.' },
  { icon: Heart, title: 'Integrity', description: 'We build relationships based on trust and transparency.' },
  { icon: Users, title: 'Collaboration', description: 'We believe in the power of teamwork.' },
  { icon: Zap, title: 'Innovation', description: 'We embrace new technologies and methodologies.' }
];

const team = [
  {
    id: 1,
    name: 'Santosh Mhaske',
    role: 'Director - Operations & Finance',
    bio: 'With over 25+ years of leadership experience as a General Manager in reputed organizations, Santosh drives our vision and strategy. His expertise in business operations, financial planning, and organizational management plays a key role in driving efficiency and long-term success.',
    image_url: '/images/team/SantoshSir.jpg'
  },
  {
    id: 2,
    name: 'Ram Auti',
    role: 'Director - Technology & Delivery',
    bio: 'With over 18 years of experience in technology leadership, Ram drives our technical strategy and oversees the successful delivery of software solutions. He ensures innovation, quality, and efficiency across all development projects while leading teams to build scalable and reliable systems.',
    image_url: '/images/team/Ram.jpg'
  },
  {
    id: 3,
    name: 'Shital Auti',
    role: 'Director - HR & Talent Acquisition',
    bio: 'Shital is responsible for leading our HR and talent acquisition strategy. With strong experience in people management, business operations, and project delivery, she ensures we connect the best talent with leading companies around the world.',
    image_url: '/images/team/Shital.jpg'
  },
  {
    id: 4,
    name: 'Jeevan Joshi',
    role: 'Director - Client Success & Partnerships',
    bio: 'Jeevan leads client engagement and strategic partnerships, ensuring exceptional customer experiences and long-term value creation. His focus on customer success, trust, and collaboration helps create sustainable growth for both clients and the organization.',
    image_url: '/images/team/JeevanJoshi.jpg'
  }
];

export const About = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const statsRes = await axios.get(`${API}/stats`);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0f1a] pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32" data-testid="about-hero">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg"
            alt="Team"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a] via-[#0a0f1a]/95 to-[#0a0f1a]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
            About Us
          </span>
          <h1 className="heading-1 mb-6">
            Your Trusted Technology Partner
          </h1>
          <p className="body-text max-w-2xl mx-auto">
            Since 2015, Sharebox Technologies has been at the forefront of connecting exceptional 
            talent with innovative companies, while delivering cutting-edge software solutions.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="service-card" data-testid="mission-card">
              <div className="icon-container mb-6">
                <Target size={24} />
              </div>
              <h3 className="heading-3 mb-4">Our Mission</h3>
              <p className="text-slate-400 leading-relaxed">
                To empower businesses through exceptional talent acquisition and innovative 
                software solutions. We bridge the gap between skilled professionals and 
                organizations seeking growth.
              </p>
            </div>

            <div className="service-card" data-testid="vision-card">
              <div className="icon-container-orange mb-6">
                <Eye size={24} />
              </div>
              <h3 className="heading-3 mb-4">Our Vision</h3>
              <p className="text-slate-400 leading-relaxed">
                To be the global leader in technology staffing and software services, 
                recognized for our commitment to quality, innovation, and the success 
                of our clients and candidates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-[#1e293b]" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="stat-number text-[#0088CC]">15+</div>
              <div className="text-slate-500 text-sm mt-1">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-[#0088CC]">500+</div>
              <div className="text-slate-500 text-sm mt-1">Placements Made</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-[#0088CC]">100+</div>
              <div className="text-slate-500 text-sm mt-1">Clients Served</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-[#FF6600]">50+</div>
              <div className="text-slate-500 text-sm mt-1">Team Members</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
              Our Values
            </span>
            <h2 className="heading-2">What Drives Us</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={value.title}
                className="service-card text-center"
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <div className="icon-container mx-auto mb-4">
                  <value.icon size={24} />
                </div>
                <h4 className="font-semibold text-white mb-2">{value.title}</h4>
                <p className="text-slate-500 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding border-t border-[#1e293b]" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
                Why Choose Us
              </span>
              <h2 className="heading-2 mb-6">Your Success Is Our Priority</h2>
              <p className="text-slate-400 mb-10 leading-relaxed">
                With nearly a decade of experience, we've developed a deep understanding of 
                what makes tech teams successful. Our dual expertise in recruitment and 
                software development gives us unique insights into both sides of the equation.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#0088CC]/10 border border-[#0088CC]/30 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="text-[#0088CC]" size={22} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Rigorous vetting process for all candidates</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#0088CC]/10 border border-[#0088CC]/30 flex items-center justify-center flex-shrink-0">
                    <Zap className="text-[#0088CC]" size={22} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Fast turnaround - average 2-week placement</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#0088CC]/10 border border-[#0088CC]/30 flex items-center justify-center flex-shrink-0">
                    <UserCheck className="text-[#0088CC]" size={22} />
                  </div>
                  <div>
                    <p className="text-white font-medium">98% client satisfaction rate</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#0088CC]/10 border border-[#0088CC]/30 flex items-center justify-center flex-shrink-0">
                    <Users className="text-[#0088CC]" size={22} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Dedicated account managers for each client</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHByb2Zlc3Npb25hbCUyMG1lZXRpbmd8ZW58MHx8fHwxNzcyOTI4OTQyfDA&ixlib=rb-4.1.0&q=85"
                  alt="Business handshake"
                  className="w-full aspect-[4/3] object-cover rounded-2xl"
                />
              </div>
              {/* Floating card - bottom left corner */}
              <div className="absolute -bottom-6 -left-6 p-5 rounded-xl bg-[#111827]/70 backdrop-blur-md border border-[#0088CC]/20 shadow-2xl shadow-[#0088CC]/10">
                <div className="text-[#0088CC] text-3xl font-bold mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                  15+
                </div>
                <div className="text-white font-semibold text-sm mb-1">Years of Excellence</div>
                <div className="text-slate-400 text-xs">Trusted by Fortune 500 companies</div>
              </div>
            </div>
          </div>
        </div>
      </section>
       
      <section className="section-padding border-t border-[#1e293b]" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
              Leadership
            </span>
            <h2 className="heading-2">Meet Our Team</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member) => (
              <div 
                key={member.id}
                className="text-center"
                data-testid={`team-member-${member.id}`}
              >
                <div className="relative mb-4 overflow-hidden rounded-xl">
                  <img 
                    src={member.image_url}
                    alt={member.name}
                    className="w-full aspect-square object-cover"
                  />
                </div>
                <h4 className="font-semibold text-white">{member.name}</h4>
                <p className="text-[#0088CC] text-sm mb-2">{member.role}</p>
                <p className="text-slate-500 text-sm">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
   

      {/* CTA */}
      <section className="section-padding border-t border-[#1e293b]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="heading-2 mb-6">Ready to Work With Us?</h2>
          <p className="body-text mb-8">
            Let's discuss how we can help you achieve your technology goals.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-[#0088CC] hover:bg-[#0099dd] text-white font-medium px-8 py-6 text-base rounded-lg"
              data-testid="about-cta-btn"
            >
              Get in Touch
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
